import React, { useEffect, useState } from 'react';
import { TrendingUp, Award, Target, ChevronDown, ChevronUp, BookOpen } from 'lucide-react';
import { fetchBenchmarkData, fetchFinancialGoals } from '../api';

const PortfolioStoryWidget = ({ userId, portfolioData, useCaseId = '1' }) => {
    const [storyData, setStoryData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        const fetchPortfolioStory = async () => {
            try {
                setIsLoading(true);
                
                // Fetch benchmark data for comparison
                const benchmarkData = await fetchBenchmarkData('SP500', '2026-01-01', '2026-01-30', userId);
                
                // Calculate benchmark return (YTD 2026)
                let benchmarkReturn = 0;
                if (benchmarkData.length >= 2) {
                    const startPrice = parseFloat(benchmarkData[0].adj_close);
                    const endPrice = parseFloat(benchmarkData[benchmarkData.length - 1].adj_close);
                    benchmarkReturn = ((endPrice - startPrice) / startPrice) * 100;
                }
                
                // Fetch financial goals
                const goalsData = await fetchFinancialGoals(userId, useCaseId);
                
                // Transform goals data to match component format
                const transformedGoals = goalsData.map(goal => {
                    const current = parseFloat(goal.current_amount) || 0;
                    const target = parseFloat(goal.target_amount) || 1;
                    const progress = Math.round((current / target) * 100);
                    
                    // Determine status based on progress and target date
                    let status = 'on_track';
                    if (progress >= 90) status = 'ahead';
                    else if (progress < 50) status = 'behind';
                    
                    return {
                        goal_name: goal.goal_name,
                        current: current,
                        target: target,
                        progress: Math.min(progress, 100),
                        status: status,
                        target_date: goal.target_date
                    };
                });
                
                // Calculate portfolio performance
                const portfolioReturn = portfolioData?.ytdReturn || 0;
                const outperformance = portfolioReturn - benchmarkReturn;
                
                // Calculate starting portfolio value (3 years ago)
                // Assuming current AUM and working backwards with actual returns
                const currentAUM = portfolioData?.totalAum || 0;
                const startingAUM = 1200000; // Hardcoded for now - could be fetched from historical data
                const totalGrowth = currentAUM - startingAUM;
                
                const mockStory = {
                    journey_summary: `Your wealth management journey began 3 years ago with $${(startingAUM / 1000000).toFixed(1)}M in assets. Through strategic allocation and market appreciation, your portfolio has grown to $${(currentAUM / 1000000).toFixed(1)}M, representing a ${((totalGrowth / startingAUM) * 100).toFixed(1)}% total return.`,
                    key_milestones: [
                        {
                            date: '2023-02',
                            title: 'Partnership Begins',
                            description: 'Started wealth management relationship with initial portfolio of $1.2M',
                            impact: 'Established 70/30 equity/fixed income allocation'
                        },
                        {
                            date: '2024-06',
                            title: 'Strategic Rebalancing',
                            description: 'Rebalanced during market volatility to capture opportunities',
                            impact: 'Avoided potential $45K in losses through defensive positioning'
                        },
                        {
                            date: '2025-03',
                            title: 'Tax Optimization',
                            description: 'Implemented tax-loss harvesting strategy',
                            impact: 'Saved $23K in tax liabilities while maintaining growth trajectory'
                        },
                        {
                            date: '2026-01',
                            title: 'Portfolio Milestone',
                            description: `Portfolio crosses $${(currentAUM / 1000000).toFixed(1)}M threshold`,
                            impact: transformedGoals.length > 0 ? `On track for ${(transformedGoals[0].goal_name || '').split('(')[0].trim()}` : 'Continuing growth trajectory'
                        }
                    ],
                    benchmark_comparison: {
                        portfolio_return: portfolioReturn,
                        benchmark_return: benchmarkReturn,
                        outperformance: parseFloat(outperformance.toFixed(2)),
                        benchmark_name: 'S&P 500'
                    },
                    goals_progress: transformedGoals.length > 0 ? transformedGoals : [
                        {
                            goal_name: 'Financial Goal',
                            current: currentAUM,
                            target: currentAUM * 1.5,
                            progress: 67,
                            status: 'on_track'
                        }
                    ],
                    next_chapter: transformedGoals.length > 0 
                        ? `With continued disciplined approach and strategic planning, you are on track to achieve your ${(transformedGoals[0].goal_name || '').split('(')[0].trim()} goal.`
                        : 'Your portfolio continues to grow steadily with strategic asset allocation and market performance.'
                };

                setStoryData(mockStory);
            } catch (error) {
                console.error('Error fetching portfolio story:', error);
            } finally {
                setIsLoading(false);
            }
        };

        if (userId && portfolioData) {
            fetchPortfolioStory();
        }
    }, [userId, portfolioData, useCaseId]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };

    if (isLoading) {
        return (
            <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-md border border-slate-700/50 rounded-lg p-6 animate-pulse">
                <div className="h-6 bg-slate-700/50 rounded w-48 mb-4"></div>
                <div className="h-20 bg-slate-700/50 rounded mb-4"></div>
                <div className="grid grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-24 bg-slate-700/50 rounded"></div>
                    ))}
                </div>
            </div>
        );
    }

    if (!storyData) {
        return null;
    }

    return (
        <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-md border border-slate-700/50 rounded-lg overflow-hidden">
            {/* Header - Always Visible */}
            <div 
                className="p-6 cursor-pointer hover:bg-slate-800/50 transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <BookOpen size={24} className="text-emerald-400" />
                        <div>
                            <h2 className="text-xl font-semibold text-white">Your Portfolio Story</h2>
                            <p className="text-sm text-slate-400">Journey, milestones, and what's next</p>
                        </div>
                    </div>
                    <button className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors">
                        <span className="text-sm font-medium">{isExpanded ? 'Show Less' : 'Read Full Story'}</span>
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                </div>

                {/* Summary - Always Visible */}
                <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <TrendingUp size={18} className="text-emerald-400" />
                            <span className="text-xs text-slate-400 uppercase tracking-wider">Total Growth</span>
                        </div>
                        <div className="text-2xl font-bold text-white">
                            {formatCurrency(portfolioData?.totalAum - 1200000)}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">Since Feb 2023</div>
                    </div>

                    <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Award size={18} className="text-primary" />
                            <span className="text-xs text-slate-400 uppercase tracking-wider">vs {storyData.benchmark_comparison.benchmark_name}</span>
                        </div>
                        <div className={`text-2xl font-bold ${storyData.benchmark_comparison.outperformance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {storyData.benchmark_comparison.outperformance >= 0 ? '+' : ''}{storyData.benchmark_comparison.outperformance.toFixed(1)}%
                        </div>
                        <div className="text-xs text-slate-400 mt-1">{storyData.benchmark_comparison.outperformance >= 0 ? 'Outperformance' : 'Underperformance'}</div>
                    </div>

                    <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Target size={18} className="text-purple-400" />
                            <span className="text-xs text-slate-400 uppercase tracking-wider">Goal Progress</span>
                        </div>
                        <div className="text-2xl font-bold text-white">
                            {storyData.goals_progress[0].progress}%
                        </div>
                        <div className="text-xs text-slate-400 mt-1">Early Retirement</div>
                    </div>
                </div>
            </div>

            {/* Expandable Content */}
            {isExpanded && (
                <div className="px-6 pb-6 space-y-6 border-t border-slate-700/50 pt-6">
                    {/* Journey Summary */}
                    <div className="bg-slate-800/40 border border-slate-700/30 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-slate-200 mb-2">Your Journey</h3>
                        <p className="text-sm text-slate-300 leading-relaxed">
                            {storyData.journey_summary}
                        </p>
                    </div>

                    {/* Key Milestones Timeline */}
                    <div>
                        <h3 className="text-sm font-semibold text-slate-200 mb-4">Key Milestones</h3>
                        <div className="space-y-3">
                            {storyData.key_milestones.map((milestone, index) => (
                                <div key={index} className="flex gap-4">
                                    <div className="flex flex-col items-center">
                                        <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                                        {index < storyData.key_milestones.length - 1 && (
                                            <div className="w-0.5 h-full bg-slate-700 mt-1"></div>
                                        )}
                                    </div>
                                    <div className="flex-1 pb-6">
                                        <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-3">
                                            <div className="flex items-center justify-between mb-1">
                                                <h4 className="text-sm font-semibold text-white">{milestone.title}</h4>
                                                <span className="text-xs text-slate-400">{formatDate(milestone.date)}</span>
                                            </div>
                                            <p className="text-xs text-slate-300 mb-2">{milestone.description}</p>
                                            <div className="text-xs text-emerald-400">
                                                Impact: {milestone.impact}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Goals Progress */}
                    <div>
                        <h3 className="text-sm font-semibold text-slate-200 mb-4">Goals Progress</h3>
                        <div className="space-y-3">
                            {storyData.goals_progress.map((goal, index) => (
                                <div key={index} className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-sm font-semibold text-white">{goal.goal_name}</h4>
                                        <span className={`text-xs px-2 py-1 rounded-full ${
                                            goal.status === 'ahead' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' :
                                            goal.status === 'on_track' ? 'bg-blue-500/20 text-primaryLight border border-blue-500/30' :
                                            'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                                        }`}>
                                            {goal.status === 'ahead' ? 'Ahead of Schedule' : 
                                             goal.status === 'on_track' ? 'On Track' : 'Needs Attention'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="flex-1 bg-slate-900/50 rounded-full h-2 overflow-hidden">
                                            <div 
                                                className="bg-gradient-to-r from-emerald-500 to-blue-500 h-full transition-all duration-500"
                                                style={{ width: `${goal.progress}%` }}
                                            ></div>
                                        </div>
                                        <span className="text-sm font-semibold text-white w-12 text-right">
                                            {goal.progress}%
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-slate-400">
                                        <span>{formatCurrency(goal.current)}</span>
                                        <span>Target: {formatCurrency(goal.target)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Next Chapter */}
                    <div className="bg-gradient-to-br from-emerald-900/30 to-blue-900/30 border border-emerald-500/30 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-emerald-300 mb-2">What's Next</h3>
                        <p className="text-sm text-slate-200 leading-relaxed">
                            {storyData.next_chapter}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortfolioStoryWidget;
